import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from '@remix-run/react';
import type { Feed, FeedEntry } from '~/services/layout';
import { ActionRegistry } from '~/hooks/actions/ActionRegistry.ts';
import { ActionResult } from '~/hooks/actions/enums';
import type { ActionHandler } from '~/hooks/actions/types';
import { useIsLoggedIn } from '~/hooks/use-is-logged-in.ts';
import logger from '~/services/logger';
import PopupMenu from '../index.tsx';
import type { PopupMenuOpenRequest } from '../types.ts';
import { usePopupMenuActionsFeed } from '../hooks/use-popup-menu-actions-feed';
import type { PopupMenuActionsSource } from '../hooks/use-popup-menu-availability';
import { buildPowerCellRightClickContextualEntry } from '../utils/contextual-action-policy';
import {
  HIDE_POPUP_MENU_ACTION,
  SHOW_POPUP_MENU_ACTION,
  type HidePopupMenuOptions,
  type PopupMenuFavoriteSource,
  type PopupMenuFavoriteState,
  type PopupMenuFavoriteStateListener,
  type PopupMenuRequestContent,
  type ShowPopupMenuOptions,
} from './types';

type PopupMenuHostRequest = ShowPopupMenuOptions & {
  requestId: number;
  /** the route the request was made on; the menu is not shown on other routes */
  pathname: string;
  /** identifies the mounted PopupMenu; kept while trigger and content stay the same */
  instanceKey: number;
};

type IsSameRequestContentArgs = {
  current: PopupMenuRequestContent;
  next: PopupMenuRequestContent;
};

type ResolveMenuInstanceKeyArgs = {
  current: PopupMenuHostRequest | null;
  next: ShowPopupMenuOptions;
  pathname: string;
  requestId: number;
};

type HostedPopupMenuProps = {
  request: PopupMenuHostRequest;
  /** resolved by the long-lived host, so a freshly mounted menu does not start "logged out" */
  isLoggedIn: boolean | null;
};

type UseEntryPopupMenuFeedArgs = {
  entry: FeedEntry;
  actionsSource: PopupMenuActionsSource;
  favorite?: PopupMenuFavoriteSource;
  isLoggedIn: boolean | null;
};

const UNAVAILABLE_FAVORITE_STATE: PopupMenuFavoriteState = {
  isFavorite: false,
  isAvailable: false,
};

/**
 * Subscription used when a request brings no favourite source.
 * @returns a no-op unsubscribe
 */
function subscribeToNoFavoriteSource(
  _listener: PopupMenuFavoriteStateListener
): () => void {
  return (): void => {};
}

/**
 * Snapshot used when a request brings no favourite source.
 * @returns the unavailable favourite state
 */
function getUnavailableFavoriteState(): PopupMenuFavoriteState {
  return UNAVAILABLE_FAVORITE_STATE;
}

/**
 * Whether two requests show the same menu content: the same entry with the
 * same actions source, or the same feed.
 * @returns true when the content matches
 */
function isSameRequestContent({
  current,
  next,
}: IsSameRequestContentArgs): boolean {
  if (current.kind === 'entry' && next.kind === 'entry') {
    return (
      current.entry.id === next.entry.id &&
      current.actionsSource === next.actionsSource
    );
  }

  if (current.kind === 'feed' && next.kind === 'feed') {
    return current.feed === next.feed;
  }

  return false;
}

/**
 * Resolves which PopupMenu instance a new request goes to. A request with the
 * route, trigger, UI component and content of the current one keeps its
 * instance, so the menu toggles its own state instead of being remounted.
 * @returns the current instance key, or the new request id for a new instance
 */
function resolveMenuInstanceKey({
  current,
  next,
  pathname,
  requestId,
}: ResolveMenuInstanceKeyArgs): number {
  if (!current) {
    return requestId;
  }

  const isSameMenuInstance: boolean =
    current.pathname === pathname &&
    current.triggerElement === next.triggerElement &&
    current.uiComponent.id === next.uiComponent.id &&
    isSameRequestContent({ current: current.content, next: next.content });

  return isSameMenuInstance ? current.instanceKey : requestId;
}

/**
 * Subscribes to the live Favourite state of the requesting cell.
 * TODO: replace with `useFavoriteState(entry.id)` once FavoriteController
 * lands on main.
 * @param favorite - the cell's favourite source, if any
 * @returns the current favourite state
 */
function useRequestFavoriteState(
  favorite: PopupMenuFavoriteSource | undefined
): PopupMenuFavoriteState {
  return useSyncExternalStore(
    favorite?.subscribe ?? subscribeToNoFavoriteSource,
    favorite?.getState ?? getUnavailableFavoriteState,
    getUnavailableFavoriteState
  );
}

/**
 * Resolves the menu feed of a cell request: the configured popup actions
 * (with live Favourite state) or the entry's own right-click actions.
 * @returns the single-entry popup feed
 */
function useEntryPopupMenuFeed({
  entry,
  actionsSource,
  favorite,
  isLoggedIn,
}: UseEntryPopupMenuFeedArgs): Feed {
  const favoriteState: PopupMenuFavoriteState =
    useRequestFavoriteState(favorite);

  const { feed: configuredFeed } = usePopupMenuActionsFeed({
    entry,
    isFavorite: favoriteState.isFavorite,
    favoritesAvailable: favoriteState.isAvailable,
    isLoggedIn: Boolean(isLoggedIn),
  });

  const entryActionsFeed: Feed = useMemo(
    () =>
      ({
        entry: [buildPowerCellRightClickContextualEntry({ entry }).entry],
      } as Feed),
    [entry]
  );

  return actionsSource === 'configured' ? configuredFeed : entryActionsFeed;
}

/**
 * Converts a host request into the PopupMenu open request (stable per request id).
 * @returns the open request
 */
function useOpenRequest(request: PopupMenuHostRequest): PopupMenuOpenRequest {
  const { requestId, triggerElement, source, pointer, quickActionAlias } =
    request;

  return useMemo(
    () => ({ requestId, triggerElement, source, pointer, quickActionAlias }),
    // A request is immutable; its id identifies it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [requestId]
  );
}

/**
 * Popup Menu for a cell request (entry content).
 */
function EntryPopupMenu({
  request,
  isLoggedIn,
}: HostedPopupMenuProps): JSX.Element | null {
  const openRequest: PopupMenuOpenRequest = useOpenRequest(request);

  if (request.content.kind !== 'entry') {
    return null;
  }

  return (
    <EntryPopupMenuContent
      request={request}
      entry={request.content.entry}
      actionsSource={request.content.actionsSource}
      favorite={request.content.favorite}
      isLoggedIn={isLoggedIn}
      openRequest={openRequest}
    />
  );
}

/**
 * Renders PopupMenu with the resolved entry feed.
 */
function EntryPopupMenuContent({
  request,
  entry,
  actionsSource,
  favorite,
  isLoggedIn,
  openRequest,
}: HostedPopupMenuProps &
  UseEntryPopupMenuFeedArgs & {
    openRequest: PopupMenuOpenRequest;
  }): JSX.Element {
  const feed: Feed = useEntryPopupMenuFeed({
    entry,
    actionsSource,
    favorite,
    isLoggedIn,
  });

  return (
    <PopupMenu
      uiComponent={request.uiComponent}
      feed={feed}
      hideTriggerButton={true}
      rootZIndex={request.zIndex}
      openRequest={openRequest}
      favoriteAction={favorite?.toggle}
      isLoggedIn={isLoggedIn}
    />
  );
}

/**
 * Popup Menu for a request that brings its own feed (audio player).
 */
function FeedPopupMenu({
  request,
  isLoggedIn,
}: HostedPopupMenuProps): JSX.Element | null {
  const openRequest: PopupMenuOpenRequest = useOpenRequest(request);

  if (request.content.kind !== 'feed') {
    return null;
  }

  return (
    <PopupMenu
      uiComponent={request.uiComponent}
      feed={request.content.feed}
      hideTriggerButton={true}
      rootZIndex={request.zIndex}
      openRequest={openRequest}
      isLoggedIn={isLoggedIn}
    />
  );
}

/**
 * The app-wide Popup Menu Host: handles Show/Hide Popup Menu requests and
 * presents a single PopupMenu anchored to the request's trigger or pointer.
 */
export function PopupMenuHost(): JSX.Element | null {
  const [request, setRequest] = useState<PopupMenuHostRequest | null>(null);
  const requestIdRef = useRef<number>(0);
  const location = useLocation();
  const pathnameRef = useRef<string>(location.pathname);
  pathnameRef.current = location.pathname;
  const { isLoggedIn } = useIsLoggedIn();

  useEffect(() => {
    const showPopupMenuHandler: ActionHandler<ShowPopupMenuOptions> = (
      action
    ) => {
      const options: ShowPopupMenuOptions | undefined = action.options;

      if (!options?.triggerElement) {
        logger.warn('PopupMenuHost.showPopupMenu: no trigger element');
        return ActionResult.Error;
      }

      requestIdRef.current += 1;
      const requestId: number = requestIdRef.current;
      const pathname: string = pathnameRef.current;

      setRequest((currentRequest) => ({
        ...options,
        requestId,
        pathname,
        instanceKey: resolveMenuInstanceKey({
          current: currentRequest,
          next: options,
          pathname,
          requestId,
        }),
      }));

      return ActionResult.Success;
    };

    const hidePopupMenuHandler: ActionHandler<HidePopupMenuOptions> = (
      action
    ) => {
      const owner: string | undefined = action.options?.owner;

      setRequest((currentRequest: PopupMenuHostRequest | null) =>
        owner && currentRequest?.owner !== owner ? currentRequest : null
      );

      return ActionResult.Success;
    };

    const registry: ActionRegistry = ActionRegistry.getInstance();
    const unregisterShow = registry.register(
      SHOW_POPUP_MENU_ACTION,
      showPopupMenuHandler
    );
    const unregisterHide = registry.register(
      HIDE_POPUP_MENU_ACTION,
      hidePopupMenuHandler
    );

    return (): void => {
      unregisterShow();
      unregisterHide();
    };
  }, []);

  useEffect(() => {
    setRequest((currentRequest) =>
      currentRequest?.pathname === location.pathname ? currentRequest : null
    );
  }, [location.pathname]);

  if (!request || request.pathname !== location.pathname) {
    return null;
  }

  const hostedPopupMenu: JSX.Element =
    request.content.kind === 'feed' ? (
      <FeedPopupMenu
        key={request.instanceKey}
        request={request}
        isLoggedIn={isLoggedIn}
      />
    ) : (
      <EntryPopupMenu
        key={request.instanceKey}
        request={request}
        isLoggedIn={isLoggedIn}
      />
    );

  return createPortal(hostedPopupMenu, document.body);
}
